import type { User } from "./types";
import { appStorage, AppStorageKeys } from "@src/services/app-storage";

type MaybeUser = User | null;

const overlay = (incoming: User, prior: MaybeUser): User => {
  if (!prior) return incoming;
  return {
    ...incoming,
    email: incoming.email?.trim() || prior.email || "",
    name: incoming.name ?? prior.name,
    picture: incoming.picture ?? prior.picture,
    // Preserve roles across a re-merge: `incoming` is typically built from the
    // decoded JWT *profile* which does not carry the `urn:visioni:roles` claim
    // (roles are attached separately via parseRolesFromAccessToken). Without
    // this, a merge would transiently drop `roles`, briefly making support-only
    // gates (e.g. useIsSupportUser) read false and permanently bail out on a
    // cold deep-link before roles are re-attached. Fall back to the prior roles
    // whenever the incoming object hasn't got them yet.
    roles: incoming.roles ?? prior.roles,
  };
};

export const mergeUser = (incoming: MaybeUser, prior: MaybeUser): MaybeUser => {
  if (!incoming) return prior;
  return overlay(incoming, prior);
};

export const withCachedUser = (user: User): User =>
  mergeUser(user, appStorage.get<User>(AppStorageKeys.AUTH_USER)) ?? user;
